// src/pages/Privacy.tsx
//
// Privacy Policy. Content sourced from the canonical document
// (Astra Tech, LLC operating as Instant Tours). Format intentionally
// uses clean MUI typography rather than dumping raw doc structure —
// readability over fidelity-to-Word.
//
// Version is stamped on user consent records (gdprConsent
// .privacyPolicyVersion in authStore). Bump on substantive content
// changes so existing users can be reminded to re-review.

import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Alert,
  Link,
} from '@mui/material';

const UPDATED = '2026-04-30';
const VERSION = '3.0';

// Compact bullet list using semantic <ul> rather than MUI's List —
// MUI List has heavy padding that fights with body copy density on
// long policy pages.
const Bullets: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <Box
    component="ul"
    sx={{
      pl: 3,
      my: 1,
      '& li': { mb: 0.5, lineHeight: 1.6 },
    }}
  >
    {items.map((it, i) => (
      <li key={i}>
        <Typography component="span" variant="body2">
          {it}
        </Typography>
      </li>
    ))}
  </Box>
);

const Section: React.FC<{ n: string; title: string; children: React.ReactNode }> = ({
  n,
  title,
  children,
}) => (
  <Box sx={{ mt: 4 }}>
    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1.5 }}>
      {n}. {title}
    </Typography>
    {children}
  </Box>
);

const Sub: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box sx={{ mt: 2.5 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.75 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.65 }}>
    {children}
  </Typography>
);

export const Privacy: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={1} sx={{ p: { xs: 3, md: 4 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
          Privacy Policy
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Effective {UPDATED} · Version {VERSION}
        </Typography>

        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            AI-generated content notice
          </Typography>
          <Typography variant="body2">
            Instant Tours uses artificial intelligence that can produce
            inaccurate, misleading, or fabricated information. The Service is
            for general information and entertainment only — it is not a
            navigation system, safety tool, or source of professional advice.
            Always verify independently and consult qualified professionals
            for any decision that matters.
          </Typography>
        </Alert>

        <Section n="1" title="Introduction">
          <P>
            This Privacy Policy explains how Astra Tech, LLC, doing business
            as Instant Tours (“Instant Tours,” “we,” “us,” or “our”), collects,
            uses, processes, shares, and protects your information when you
            use our websites, applications, products, services, and
            technologies (collectively, the “Service”).
          </P>
          <P>
            This Privacy Policy applies to all users of the Service, whether
            you create an account or interact with the Service without logging
            in. We encourage you to read it carefully so you understand how
            your information is handled and the choices available to you.
          </P>

          <Sub title="Scope of this Policy">
            <Bullets
              items={[
                'The information we collect and receive',
                'How we use and process that information',
                'How we share information with partners, service providers, and third parties',
                'How long we retain information',
                'How we transfer information globally',
                'Your rights and choices regarding your information',
              ]}
            />
            <P>
              This Privacy Policy should be read together with our Terms of
              Use, which govern your use of the Service.
            </P>
          </Sub>

          <Sub title="Our approach to data">
            <P>
              We design our systems to provide useful, personalized, and
              location-aware experiences, including tours, recommendations,
              content, and advertising. To do this, we may:
            </P>
            <Bullets
              items={[
                'Collect and analyze information about how you use the Service',
                'Use automated systems, including artificial intelligence and machine learning, to personalize content, recommendations, and advertising',
                'Combine information across devices, sessions, and services we operate or integrate with, where applicable',
                'Work with partners, vendors, researchers, and third parties to operate, improve, and expand the Service',
              ]}
            />
            <P>
              Where appropriate, we use aggregated, de-identified, or
              anonymized information to reduce the use of data that directly
              identifies individuals.
            </P>
          </Sub>

          <Sub title="Important notes">
            <Bullets
              items={[
                'The Service may include features that rely on location data, user-generated content, third-party data sources, and automated systems.',
                'Information available through the Service may be provided by users, partners, or third parties and may not always be accurate or up to date.',
                'Your use of the Service may involve real-world activities, and you remain responsible for your decisions and actions.',
              ]}
            />
          </Sub>

          <Sub title="Your control">
            <P>
              You have choices about how your information is used, including
              through your device settings, in-app settings, and rights
              provided under applicable laws such as the General Data
              Protection Regulation (GDPR). We provide tools and options to
              access, update, manage, or delete your information; these are
              described later in this Privacy Policy.
            </P>
          </Sub>
        </Section>

        <Section n="2" title="What information we collect">
          <P>
            We collect and receive different types of information depending
            on how you use the Service, the features you access, and the
            choices you make.
          </P>

          <Sub title="Information you provide">
            <Bullets
              items={[
                'Account information, such as your name, username, email address, phone number, and profile details',
                'Content you create or share, such as tours, routes, photos, videos, comments, reviews, messages, or other materials',
                'Communications, including messages you send to us or through the Service, support requests, and feedback',
                'Transaction information, if you make purchases, bookings, or other transactions through the Service',
                'Information you provide to partners, where you interact with businesses, advertisers, or integrated services through the Service',
              ]}
            />
          </Sub>

          <Sub title="Activity and usage information">
            <Bullets
              items={[
                'The content you view, interact with, or engage with',
                'Tours, locations, or experiences you search for, view, or save',
                'Features and functionality you use',
                'Actions you take, such as clicks, likes, shares, or navigation within the Service',
                'The time, frequency, and duration of your activity',
                'How you interact with advertisements, sponsored content, or commercial features',
              ]}
            />
          </Sub>

          <Sub title="Location information">
            <P>
              Because Instant Tours provides location-based features, we may
              collect and use:
            </P>
            <Bullets
              items={[
                'Precise location data, such as GPS data, when you enable location permissions on your device',
                'Approximate location data, such as IP address or network-based location',
                'Location-related activity, such as places you search for, visit, or interact with within the Service',
              ]}
            />
            <P>
              Location features depend on your device settings and
              permissions; you can control location access through your
              device or app settings.
            </P>
          </Sub>

          <Sub title="Device, technical, and log information">
            <Bullets
              items={[
                'Device type, operating system, and software versions',
                'Unique device identifiers and account identifiers',
                'IP address and network information',
                'Browser type and app version',
                'Performance data, crash reports, and diagnostic information',
                'Cookies and similar technologies used to recognize your device and store preferences',
              ]}
            />
          </Sub>

          <Sub title="Information from partners, vendors, and third parties">
            <Bullets
              items={[
                'Information about your activity on other websites, apps, or services that integrate with or interact with the Service',
                'Advertising and marketing data, including interactions with ads',
                'Demographic or interest-based information provided by partners',
                'Information from publicly available sources',
                'Information provided by businesses, creators, or other users',
              ]}
            />
            <P>
              We may combine this information with information we collect
              directly from you.
            </P>
          </Sub>

          <Sub title="Information from other users">
            <Bullets
              items={[
                'Content in which you are tagged, mentioned, or included',
                'Messages or communications sent to you',
                'Information shared about you by others through the Service',
              ]}
            />
          </Sub>

          <Sub title="Inferred and derived information">
            <P>
              We may generate or infer information about you based on your
              activity, behavior, and interactions, including preferences,
              interests, travel patterns, engagement patterns with content or
              advertisements, and aggregated or de-identified insights used
              to improve the Service.
            </P>
          </Sub>

          <Sub title="Artificial intelligence and automated processing">
            <P>
              We may use automated systems, including artificial intelligence
              and machine learning, to collect, process, and analyze
              information for purposes such as improving recommendations and
              search, enhancing content quality and relevance, supporting
              safety, moderation, and fraud detection, and developing and
              improving features, models, and technologies.
            </P>
          </Sub>

          <Sub title="Information we collect without an account">
            <P>
              We may collect certain information even if you do not create an
              account or are not logged in, including device and browser
              information, usage data related to public content, cookie and
              tracking data, and general location information.
            </P>
          </Sub>

          <Sub title="When you choose not to provide information">
            <P>
              Some information is required for the Service to function. If
              you choose not to provide it, you may not be able to access or
              use certain features.
            </P>
          </Sub>
        </Section>

        <Section n="3" title="How we use your information">
          <P>
            We use the information we collect to operate, provide, improve,
            and grow the Service, and to support safety, security, and
            business operations.
          </P>

          <Sub title="Provide and operate the Service">
            <Bullets
              items={[
                'Creating and managing your account',
                'Enabling you to create, discover, and interact with tours, locations, and content',
                'Providing navigation-related experiences and location-based features',
                'Enabling communication between users, businesses, and partners',
                'Processing transactions, bookings, or purchases where applicable',
                'Providing customer support and responding to inquiries',
              ]}
            />
          </Sub>

          <Sub title="Personalization and recommendations">
            <Bullets
              items={[
                'Recommending tours, places, and experiences',
                'Showing relevant content based on your activity, location, and preferences',
                'Customizing features, search results, and discovery experiences',
                'Ranking and prioritizing content within the Service',
              ]}
            />
          </Sub>

          <Sub title="Advertising and sponsored content">
            <Bullets
              items={[
                'Showing ads, sponsored content, and commercial opportunities',
                'Personalizing advertisements based on your activity, interests, and interactions',
                'Measuring the effectiveness of ads and campaigns',
                'Providing advertisers with aggregated insights and performance data',
              ]}
            />
            <P>
              We do not sell your personal data directly to advertisers.
              However, we use your information to help advertisers reach
              relevant audiences.
            </P>
          </Sub>

          <Sub title="Analytics, measurement, and business operations">
            <P>
              We analyze usage patterns and user behavior, monitor
              performance, stability, and reliability, conduct internal
              reporting, forecasting, and business analytics, and measure
              engagement, growth, and feature effectiveness. We may use
              aggregated or de-identified data for these purposes.
            </P>
          </Sub>

          <Sub title="Research, development, and innovation">
            <Bullets
              items={[
                'Building and improving features and functionality',
                'Training, testing, and improving artificial intelligence and machine learning systems',
                'Conducting research and development initiatives',
                'Collaborating with partners, researchers, and organizations where appropriate',
              ]}
            />
            <P>
              This may include the use of aggregated, anonymized, or, where
              permitted, identifiable data.
            </P>
          </Sub>

          <Sub title="Safety, security, and integrity">
            <Bullets
              items={[
                'Detecting, preventing, and addressing fraud, abuse, and harmful activity',
                'Enforcing our Terms, policies, and applicable laws',
                'Investigating suspicious activity or violations',
                'Protecting users, the public, and our systems',
              ]}
            />
            <P>This may involve automated systems as well as manual review.</P>
          </Sub>

          <Sub title="Communication">
            <Bullets
              items={[
                'Sending service-related messages, updates, and notifications',
                'Responding to support requests or inquiries',
                'Providing information about new features, products, or services',
                'Sending marketing communications, where permitted by law and based on your preferences',
              ]}
            />
          </Sub>

          <Sub title="Cross-service and partner integrations">
            <P>
              Where applicable, we may use your information to enable
              integrations with third-party services, applications, or
              platforms; provide seamless experiences across related services
              or products; and support account linking or identity matching
              across systems.
            </P>
          </Sub>

          <Sub title="Legal and compliance">
            <P>
              We may use your information to comply with applicable laws,
              regulations, and legal obligations; respond to legal requests
              or government authorities; enforce agreements and protect our
              rights; and prevent harm, fraud, or illegal activity.
            </P>
          </Sub>

          <Sub title="Automated processing and decision-making">
            <P>
              We may use automated systems, including AI and machine
              learning, to process your information for personalization,
              advertising optimization, fraud detection and security, and
              content moderation and ranking. These systems may operate
              without human intervention but may, in some cases, be
              supplemented by human review.
            </P>
          </Sub>

          <Sub title="De-identified and aggregated data">
            <P>
              We may use information that has been aggregated, anonymized, or
              de-identified for any lawful purpose, including analytics and
              reporting, research and development, improving products and
              services, and commercial and business purposes.
            </P>
          </Sub>
        </Section>

        <Section n="4" title="How we share your information">
          <Sub title="With other users">
            <Bullets
              items={[
                'Content you create, upload, or share',
                'Your profile information (such as username or profile image)',
                'Your interactions with tours, locations, and content',
                'Comments, reviews, or contributions you make',
              ]}
            />
            <P>
              Any information you choose to make public may be viewed,
              shared, or redistributed by others.
            </P>
          </Sub>

          <Sub title="With service providers and vendors">
            <P>
              We share information with third-party service providers who
              help us operate the Service: cloud hosting and infrastructure
              providers, data storage and database providers, analytics and
              measurement providers, customer support tools, payment
              processors, security and fraud-prevention services, and
              content delivery and performance optimization providers. These
              providers process information on our behalf and are required
              to protect it.
            </P>
          </Sub>

          <Sub title="With advertising, marketing, and measurement partners">
            <P>
              To support monetization and growth, we may share information
              with advertisers and advertising platforms, marketing and
              campaign management partners, measurement and analytics
              vendors, and attribution and performance tracking providers.
              This may include aggregated or de-identified data, device
              identifiers or signals, and information about interactions
              with ads or content. We do not share information that
              directly identifies you (such as your name or email) with
              advertisers unless you explicitly permit it.
            </P>
          </Sub>

          <Sub title="With business partners and integrations">
            <P>
              If you interact with third-party services through Instant
              Tours, we may share information with those partners — for
              example, travel providers, venues, or tour operators; mapping,
              location, or geospatial services; booking, reservation, or
              ticketing providers; integrated applications, APIs, or
              connected platforms. These partners operate under their own
              terms and privacy policies.
            </P>
          </Sub>

          <Sub title="With affiliates and related entities">
            <P>
              We may share information within Astra Tech, LLC and its
              affiliates, subsidiaries, or related entities for purposes
              consistent with this Privacy Policy.
            </P>
          </Sub>

          <Sub title="For research and development">
            <P>
              We may share information with research partners, academic
              institutions, industry collaborators, and external advisors or
              review bodies. This may include aggregated, anonymized, or
              limited datasets to advance research and innovation, improve
              technology including AI systems, and support safety, ethics,
              and oversight initiatives.
            </P>
          </Sub>

          <Sub title="For legal, safety, and compliance reasons">
            <P>
              We may share your information when we believe it is necessary
              to comply with applicable laws, regulations, or legal
              processes; respond to lawful requests from government
              authorities; enforce our Terms or policies; detect, prevent,
              or address fraud, abuse, or security issues; or protect the
              rights, property, or safety of users, the public, or our
              company.
            </P>
          </Sub>

          <Sub title="In connection with business transfers">
            <P>
              If Instant Tours or Astra Tech, LLC is involved in a merger,
              acquisition, financing, reorganization, or sale of assets,
              your information may be transferred as part of that
              transaction, subject to applicable laws.
            </P>
          </Sub>

          <Sub title="With your consent or direction">
            <P>
              We may share your information with third parties when you
              request or authorize us to do so, when you choose to connect
              or integrate third-party services, or when you participate in
              features that involve sharing information externally.
            </P>
          </Sub>

          <Sub title="Aggregated and de-identified information">
            <P>
              We may share aggregated, anonymized, or de-identified
              information that cannot reasonably be used to identify you
              for any lawful purpose.
            </P>
          </Sub>
        </Section>

        <Section n="5" title="How we transfer and store information">
          <Sub title="Global infrastructure">
            <P>
              Your information may be stored and processed on servers and
              systems located in the United States, the United Kingdom, the
              European Economic Area (EEA), and other countries where our
              service providers or partners operate. These locations may
              have different data protection laws than your country of
              residence.
            </P>
          </Sub>

          <Sub title="Cross-border data transfers">
            <P>
              By using the Service, you acknowledge that your information
              may be transferred to and processed in countries outside of
              your country of residence. Where required by applicable law,
              we implement safeguards such as standard contractual clauses
              or equivalent legal mechanisms, data processing agreements
              with service providers, and security and confidentiality
              obligations.
            </P>
          </Sub>

          <Sub title="Data storage">
            <P>
              We store information using third-party infrastructure and
              service providers, including cloud hosting platforms,
              database and storage systems, and content delivery networks
              (CDNs). We select providers designed to support secure,
              reliable, and scalable operations.
            </P>
          </Sub>

          <Sub title="Data retention">
            <P>
              We retain your information for as long as necessary to provide
              and operate the Service, maintain your account and user
              experience, comply with legal obligations, resolve disputes
              and enforce agreements, and support security, fraud
              prevention, and system integrity. Retention periods may vary
              depending on the type of information and how it is used.
            </P>
          </Sub>

          <Sub title="Deletion and backup systems">
            <P>
              When you delete your account or content, the deletion process
              may not be immediate. Information may remain in backups or
              recovery systems for a limited period, and certain
              information may be retained where required for legal,
              security, or operational purposes.
            </P>
          </Sub>

          <Sub title="Security measures">
            <P>
              We implement reasonable administrative, technical, and
              organizational safeguards designed to protect your
              information, including encryption of data in transit where
              appropriate, access controls and authentication mechanisms,
              monitoring and detection of security threats, and secure
              infrastructure and hosting environments. However, no system
              can be guaranteed to be completely secure.
            </P>
          </Sub>

          <Sub title="International use">
            <P>
              If you access the Service from outside the United States, you
              understand that your information may be transferred to and
              processed in the United States or other jurisdictions where
              our systems or partners operate.
            </P>
          </Sub>
        </Section>

        <Section n="6" title="Your rights and choices">
          <P>
            You have certain rights and choices regarding your information.
            These may vary depending on your location and applicable laws.
          </P>

          <Sub title="Access and control">
            <P>
              You may be able to access the information associated with
              your account, update or correct your information, delete
              content you have created or shared, and manage your account
              settings and preferences — typically directly within the
              Service or by contacting us.
            </P>
          </Sub>

          <Sub title="Account deletion">
            <P>
              You may request to delete your account at any time. When you
              delete your account, your profile and content will no longer
              be visible to other users, and we will begin the process of
              removing your information from our systems. Some information
              may be retained for a limited time in backups or as required
              for legal, security, or operational purposes.
            </P>
          </Sub>

          <Sub title="Data portability">
            <P>
              Where required by applicable law, you may have the right to
              request a copy of your information in a structured, commonly
              used, and machine-readable format.
            </P>
          </Sub>

          <Sub title="Restriction and objection">
            <P>
              Depending on your jurisdiction, you may have the right to
              object to certain processing of your information, request
              that we limit how your information is used, and withdraw
              consent where processing is based on consent.
            </P>
          </Sub>

          <Sub title="Advertising choices">
            <P>
              You may be able to manage ad preferences within the Service,
              limit certain types of personalized advertising through
              device or platform settings, and opt out of certain tracking
              technologies through your browser or device. Even if you opt
              out of personalized ads, you may still see general or
              contextual advertising.
            </P>
          </Sub>

          <Sub title="Location information">
            <P>
              You can control location-related data by adjusting your
              device settings (such as GPS permissions) and managing
              permissions within the Service. Disabling location services
              may limit certain features.
            </P>
          </Sub>

          <Sub title="Communications preferences">
            <P>
              You can control how we communicate with you by managing
              notification settings within the Service and opting out of
              marketing communications where applicable. You may still
              receive important service-related communications.
            </P>
          </Sub>

          <Sub title="Exercising your rights">
            <P>
              To exercise your rights, use available settings within the
              Service or contact us using the details provided in this
              Privacy Policy. We may need to verify your identity before
              fulfilling certain requests.
            </P>
          </Sub>

          <Sub title="Regional rights (EEA, UK, and similar jurisdictions)">
            <Bullets
              items={[
                'The right to access your personal data',
                'The right to correct inaccurate data',
                'The right to request deletion (the “right to be forgotten”)',
                'The right to restrict or object to processing',
                'The right to data portability',
                'The right to lodge a complaint with a data protection authority',
              ]}
            />
          </Sub>
        </Section>

        <Section n="7" title="Data retention">
          <P>
            We retain your information only for as long as necessary to
            fulfill the purposes described in this Privacy Policy, unless a
            longer retention period is required or permitted by law. We
            consider the purpose for collection, the nature and sensitivity
            of the data, the need to operate and improve the Service, legal
            and regulatory requirements, dispute resolution and enforcement
            needs, and security and fraud-prevention considerations.
          </P>
          <P>
            We generally retain account-related information for as long as
            your account remains active. If you delete your account, we
            will begin the process of deleting or anonymizing your
            information; some information may remain for a limited period
            in backup or recovery systems, and certain information may be
            retained where required for legal or legitimate business
            purposes.
          </P>
          <P>
            We may retain and use information that has been anonymized,
            aggregated, or de-identified — so that it can no longer
            reasonably be associated with you — indefinitely, for analytics
            and research, product development and improvement, and
            commercial purposes.
          </P>
        </Section>

        <Section n="8" title="Security">
          <P>
            We use a combination of administrative, technical, and
            organizational safeguards, which may include encryption of data
            in transit where appropriate, access controls and
            authentication mechanisms, monitoring systems to detect and
            prevent unauthorized access, secure infrastructure and hosting
            environments, and regular evaluation and improvement of our
            security practices.
          </P>
          <P>
            No system or method of transmission over the internet is
            completely secure. We cannot guarantee absolute security, and
            you provide and transmit information at your own risk. You are
            responsible for maintaining the security of your account —
            keep your login credentials confidential, use strong passwords
            and secure devices, and do not share access to your account
            with unauthorized parties. If you believe your account has
            been compromised, contact us immediately.
          </P>
        </Section>

        <Section n="9" title="Children’s privacy">
          <P>
            <strong>
              The Instant Tours Service is not intended for individuals
              under the age of 16. You must be at least 16 years old to
              create an account or use the Service. By using the Service,
              you represent and warrant that you meet this requirement.
            </strong>
          </P>
          <P>
            We do not knowingly collect personal information from
            individuals under 16. If we become aware that we have collected
            personal information from someone under 16, we will take steps
            to delete that information and may disable or remove the
            associated account.
          </P>
          <P>
            If you believe that a child under 16 has provided personal
            information to us, please contact us so we can investigate and
            take appropriate action. We reserve the right to request
            verification of age and to suspend or terminate accounts that
            violate this section.
          </P>
        </Section>

        <Section n="10" title="Changes to this Privacy Policy">
          <P>
            We may update this Privacy Policy from time to time to reflect
            changes to the Service, our practices, or applicable laws. When
            we make changes, we will update the “Effective Date” at the top
            of this Privacy Policy and may provide notice through the
            Service or by other reasonable means where required by law.
            Your continued use of the Service after any updates become
            effective constitutes your acceptance of the revised Privacy
            Policy. If you do not agree to the updated Policy, you should
            discontinue use of the Service.
          </P>
        </Section>

        <Section n="11" title="Contact information">
          <P>
            If you have questions, requests, or concerns about this Privacy
            Policy or how your information is handled, you can contact us
            at:
          </P>
          <P>
            Astra Tech, LLC<br />
            Instant Tours (DBA)<br />
            <Link href="mailto:hello@theastralabs.com">
              hello@theastralabs.com
            </Link>
          </P>
          <P>
            If you are located in the European Economic Area (EEA), the
            United Kingdom, or another jurisdiction with applicable data
            protection laws, you may also submit requests to exercise your
            data rights or contact your local data protection authority.
            We will respond to requests in accordance with applicable
            laws.
          </P>
        </Section>
      </Paper>
    </Container>
  );
};
